import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Star, Check, X, Trash2, RefreshCw, MessageSquare, BadgeCheck, ExternalLink } from 'lucide-react';
import { adminFetch } from '../../utils/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [stats, setStats] = useState({ pending: 0, approved: 0, total: 0 });

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const status = filter === 'all' ? '' : filter;
      const res = await adminFetch(`${BACKEND_URL}/api/admin/reviews?status=${status}`);
      if (res.ok) setReviews(await res.json());
    } catch { toast.error('Failed to load reviews'); }
    setLoading(false);
  }, [filter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/reviews/stats`);
      if (res.ok) setStats(await res.json());
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchReviews(); fetchStats(); }, [fetchReviews, fetchStats]);

  const setApproved = async (reviewId, approved) => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/reviews/${reviewId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_approved: approved })
      });
      if (res.ok) {
        toast.success(approved ? 'Review approved' : 'Review unapproved');
        fetchReviews(); fetchStats();
      } else toast.error('Update failed');
    } catch { toast.error('Update failed'); }
  };

  const deleteReview = async (reviewId) => {
    if (!confirm('Delete this review permanently?')) return;
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/reviews/${reviewId}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Deleted'); fetchReviews(); fetchStats(); }
    } catch { toast.error('Delete failed'); }
  };

  const TABS = [
    { id: 'pending', label: `Pending (${stats.pending})` },
    { id: 'approved', label: `Approved (${stats.approved})` },
    { id: 'all', label: `All (${stats.total})` },
  ];

  return (
    <div data-testid="admin-reviews">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Reviews</h1>
          <p className="text-gray-500 text-sm">Moderate product reviews submitted by customers</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-4">
        <div className="border-b">
          <div className="flex gap-1 px-4">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setFilter(t.id)} data-testid={`reviews-tab-${t.id}`}
                className={`px-4 py-3 text-sm whitespace-nowrap border-b-2 ${filter === t.id ? 'border-blue-600 text-blue-600 font-semibold' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-4">
          {loading ? (
            <div className="py-12 flex justify-center"><RefreshCw className="w-6 h-6 text-blue-500 animate-spin" /></div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12" data-testid="reviews-admin-empty">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-medium text-gray-900 mb-1">No {filter === 'all' ? '' : filter} reviews</h3>
              <p className="text-gray-500 text-sm">Customer reviews will appear here for moderation.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reviews.map(r => (
                <div key={r.review_id} data-testid={`admin-review-${r.review_id}`} className="border-2 border-gray-100 rounded-xl p-4 hover:border-blue-200 transition-colors">
                  <div className="flex items-start gap-4">
                    {r.product_image && (
                      <img src={r.product_image} alt={r.product_name} className="w-16 h-16 object-contain bg-gray-50 rounded-lg flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                        <div>
                          <a href={`/product/${r.product_slug || r.product_id}`} target="_blank" rel="noopener noreferrer"
                            className="text-sm font-semibold text-blue-700 hover:underline inline-flex items-center gap-1">
                            {r.product_name || r.product_id}
                            <ExternalLink size={12} />
                          </a>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm text-gray-900 font-medium">{r.user_name}</span>
                            {r.is_verified_purchase && (
                              <span className="inline-flex items-center gap-1 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">
                                <BadgeCheck size={10} /> Verified
                              </span>
                            )}
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.is_approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-800'}`}>
                              {r.is_approved ? 'Approved' : 'Pending'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex gap-0.5">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} size={13} className={i < r.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
                              ))}
                            </div>
                            <span className="text-xs text-gray-500">
                              {r.created_at && new Date(r.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-1.5">
                          {!r.is_approved && (
                            <button onClick={() => setApproved(r.review_id, true)} data-testid={`approve-${r.review_id}`}
                              className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium flex items-center gap-1">
                              <Check size={13} /> Approve
                            </button>
                          )}
                          {r.is_approved && (
                            <button onClick={() => setApproved(r.review_id, false)} data-testid={`unapprove-${r.review_id}`}
                              className="px-3 py-1.5 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-xs font-medium flex items-center gap-1">
                              <X size={13} /> Unapprove
                            </button>
                          )}
                          <button onClick={() => deleteReview(r.review_id)} data-testid={`delete-${r.review_id}`}
                            className="px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-lg border border-gray-200 text-xs">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                      {r.title && <h4 className="font-semibold text-gray-900 mb-1 text-sm">{r.title}</h4>}
                      <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">{r.review}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminReviews;
